/**
 * EVO — autentifikatsiya va ruxsat holati.
 *
 * Ruxsatlar serverdan KELADI, brauzerda hisoblanmaydi.
 * `roleCodes` dan `permissions` ni frontendda chiqarish taqiqlanadi — matritsa
 * (EVO_PERMISSION_MATRIX_v1.md §3) bitta joyda, bazada turadi.
 *
 * MUHIM: bu yerdagi `can()` — faqat INTERFEYS qulayligi uchun.
 * Haqiqiy himoya API tomonda: `PermissionsGuard` (endpoint) va `maskFields`
 * (javob serializatsiyasi). PERMISSION_MATRIX §4.2 uchta qatlamni talab qiladi,
 * UI — ulardan eng zaifi. Menyuni yashirish xavfsizlik emas, ergonomika.
 */
import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { api, ApiError, setSessionExpiredHandler, tokenStore } from './api';
import { syncLocaleFromProfile } from './i18n-context';

export interface UserScope {
  readonly scopeType: 'branch' | 'warehouse' | 'all';
  readonly scopeId: number | null;
}

/**
 * Foydalanuvchi turgan TASHKILOT — `/auth/me` javobidagi `org` (0048).
 *
 * ┌────────────────────────────────────────────────────────────────────────┐
 * │ NIMA UCHUN SESSIYADA, ALOHIDA SO'ROVDA EMAS                            │
 * │                                                                        │
 * │ Nom chop etiladigan hisobot blankasiga tushadi va u TOKEN bilan birga  │
 * │ almashishi shart: partner tashkilotiga kirib ko'rishda                 │
 * │ (`layout/partner-view.tsx`) token almashadi va o'sha yerda faqat       │
 * │ `reloadUser()` chaqiriladi. Nom boshqa so'rovdan kelsa, u              │
 * │ `queryClient.clear()` dan keyin qayta yuklanguncha ESKI qolardi —      │
 * │ ya'ni begona hisobotda bir zumga bizning nomimiz turardi.              │
 * └────────────────────────────────────────────────────────────────────────┘
 */
export interface AuthOrg {
  readonly name: string;
  /** `primary` — biz, `partner` — biz qo'ygan tashkilot. */
  readonly kind: 'primary' | 'partner';
}

/**
 * API `/auth/me` javobi. `RequestContext` (apps/api/src/common/request-context.ts)
 * bilan bir xil shakl.
 *
 * ⚠️ `tenantId` 0048 gacha bu yerda e'lon qilingan, lekin server tomonidan
 * HECH QACHON yuborilmagan edi — tip yolg'on edi. Endi `MeResponse` uni
 * haqiqatan qaytaradi (`auth.service.ts` dagi izoh).
 */
export interface AuthUser {
  readonly id: number;
  readonly tenantId: number;
  readonly email: string;
  readonly fullName: string;
  readonly locale: 'uz' | 'ru' | 'en';
  readonly roleCodes: readonly string[];
  readonly permissions: readonly string[];
  readonly scopes: readonly UserScope[];
  readonly org: AuthOrg;
}

/**
 * `/auth/login` javobi.
 *
 * `user` bu yerda ATAYLAB `AuthUser` emas: server login javobida faqat
 * `id/email/fullName/locale` qaytaradi — `permissions`, `roleCodes` va `scopes`
 * YO'Q. Tipni `AuthUser` deb e'lon qilish yolg'on bo'lardi va `permissions`
 * `undefined` bo'lgani sezilmay qolardi (`new Set(undefined)` xato bermaydi,
 * shunchaki BO'SH to'plam beradi — foydalanuvchi esa menyusiz ekran ko'radi).
 */
interface LoginResponse {
  readonly accessToken: string;
  readonly refreshToken: string;
  readonly user: Pick<AuthUser, 'id' | 'email' | 'fullName' | 'locale'>;
}

/** `/auth/me` ba'zi implementatsiyalarda `{ user }` ichida qaytaradi — ikkalasini ham qabul qilamiz. */
type MeResponse = AuthUser | { readonly user: AuthUser };

function unwrapUser(response: MeResponse): AuthUser {
  return 'user' in response ? response.user : response;
}

export interface AuthContextValue {
  readonly user: AuthUser | null;
  readonly permissions: ReadonlySet<string>;
  readonly roleCodes: readonly string[];
  /** Boshlang'ich sessiya tiklanishi tugamaganini bildiradi — bu paytda redirect qilinmaydi. */
  readonly isLoading: boolean;
  readonly login: (email: string, password: string) => Promise<void>;
  readonly logout: () => void;
  /**
   * Kontekstni SERVERDAN qayta o'qish.
   *
   * ┌────────────────────────────────────────────────────────────────────────┐
   * │ NIMA UCHUN KERAK: TOKEN ALMASHSA, RUXSATLAR HAM ALMASHADI              │
   * │                                                                        │
   * │ Partner tashkilotiga kirib ko'rishda `Authorization` boshqa tokenga    │
   * │ o'tadi va o'shandan keyin `/auth/me` BOSHQA tenant, BOSHQA ruxsat      │
   * │ to'plamini qaytaradi (faqat `.view`). Agar bu yerdagi `user` eski      │
   * │ holida qolsa, menyu va tugmalar BIZNING ruxsatlarimizni ko'rsatib     │
   * │ turardi: xodim «Saqlash» ni bosib 403 olardi va bu «tizim buzuq»       │
   * │ degan taassurot berardi. Ruxsat manbai bitta bo'lishi shart —          │
   * │ `login()` dagi bilan aynan bir mulohaza.                              │
   * └────────────────────────────────────────────────────────────────────────┘
   */
  readonly reloadUser: () => Promise<void>;
  readonly can: (permission: string) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const EMPTY_PERMISSIONS: ReadonlySet<string> = new Set<string>();
const EMPTY_ROLES: readonly string[] = [];

export function AuthProvider({ children }: { readonly children: ReactNode }): ReactNode {
  const [user, setUser] = useState<AuthUser | null>(null);
  // Refresh token bor bo'lsa — sessiyani tiklashga urinamiz, shu paytda ekran kutadi.
  const [isLoading, setIsLoading] = useState<boolean>(() => tokenStore.hasSession());

  const applyUser = useCallback((next: AuthUser) => {
    syncLocaleFromProfile(next.locale);
    setUser(next);
  }, []);

  const logout = useCallback(() => {
    tokenStore.clear();
    setUser(null);
    // Serverda sessiyani bekor qilish — javobi kutilmaydi, UI darhol yopiladi.
    // Xato bo'lsa ham foydalanuvchi uchun natija bir xil: u chiqib ketdi.
    void api.post('/auth/logout').catch(() => undefined);
  }, []);

  const reloadUser = useCallback(async (): Promise<void> => {
    const me = await api.get<MeResponse>('/auth/me');
    applyUser(unwrapUser(me));
  }, [applyUser]);

  const login = useCallback(
    async (email: string, password: string): Promise<void> => {
      const response = await api.post<LoginResponse>(
        '/auth/login',
        { email: email.trim(), password },
        { auth: false },
      );
      tokenStore.set({ accessToken: response.accessToken, refreshToken: response.refreshToken });

      /*
       * Ruxsatlar login javobida KELMAYDI (yuqoridagi izohga qarang), shuning
       * uchun to'liq kontekst darhol `/auth/me` dan olinadi.
       *
       * Bu qo'shimcha so'rov, lekin muhim ustunligi bor: ruxsat manbai BITTA
       * bo'lib qoladi. Sahifa yangilanganda ham aynan shu endpoint ishlaydi,
       * ya'ni «login qilganda bitta ruxsat to'plami, F5 dan keyin boshqasi»
       * degan farq umuman paydo bo'lolmaydi.
       */
      const me = await api.get<MeResponse>('/auth/me');
      applyUser(unwrapUser(me));
    },
    [applyUser],
  );

  // api.ts React'ni bilmaydi — sessiya tugaganda nima qilishni shu yerdan ulaymiz.
  useEffect(() => {
    setSessionExpiredHandler(() => setUser(null));
    return () => setSessionExpiredHandler(null);
  }, []);

  // React 18+ StrictMode dev'da effektni ikki marta ishga tushiradi —
  // sessiyani ikki marta tiklamaslik uchun qo'riqchi.
  const restoreStarted = useRef(false);

  useEffect(() => {
    if (restoreStarted.current) return;
    restoreStarted.current = true;

    if (!tokenStore.hasSession()) {
      setIsLoading(false);
      return;
    }

    /*
     * ╔════════════════════════════════════════════════════════════════════╗
     * ║ `cancelled` BAYROG'I OLIB TASHLANDI — u ilovani QOTIRIB QO'YARDI    ║
     * ║                                                                    ║
     * ║ Bu yerda odatiy `let cancelled = false` naqshi turardi va u         ║
     * ║ yuqoridagi `restoreStarted` qo'riqchisi bilan BIRGA halokatli edi.  ║
     * ║ StrictMode (`main.tsx`) ketma-ketligi:                              ║
     * ║                                                                    ║
     * ║   1. effekt ishga tushdi   → restoreStarted = true, so'rov ketdi    ║
     * ║   2. cleanup ishladi       → cancelled = true                       ║
     * ║   3. effekt QAYTA ishladi  → restoreStarted tufayli darhol chiqdi   ║
     * ║   4. birinchi so'rov tugadi → `finally` da `cancelled` TRUE         ║
     * ║      → `setIsLoading(false)` HECH QACHON chaqirilmadi               ║
     * ║                                                                    ║
     * ║ Natija: SESSIYASI BOR foydalanuvchi sahifani yangilaganda ekran     ║
     * ║ «Yuklanmoqda…» da abadiy qolardi. Ya'ni bu «kira olmayapman» degan  ║
     * ║ shikoyatning ikkinchi sababi — birinchisi `api.ts` dagi tarmoq      ║
     * ║ xatosi edi. Faqat dev'da ko'rinadi (prod'da StrictMode yo'q),       ║
     * ║ egasi esa aynan dev serverda ishlaydi.                             ║
     * ║                                                                    ║
     * ║ `restoreStarted` allaqachon effektni BIR MARTA ishlatadi: bir       ║
     * ║ vaqtda bittadan ortiq so'rov bo'lmaydi va `cancelled` himoya        ║
     * ║ qiladigan holat umuman yuzaga kelmaydi. Ikki qo'riqchi bir narsani  ║
     * ║ qo'riqlaganda, biri ikkinchisini o'ldiradi.                         ║
     * ╚════════════════════════════════════════════════════════════════════╝
     *
     * `/auth/me` access token'siz 401 qaytaradi, api.ts esa avtomatik refresh
     * qilib so'rovni qaytaradi — sahifa yangilanganda sessiya shu chaqiruv
     * orqali tiklanadi.
     */
    void api
      .get<MeResponse>('/auth/me')
      .then((response) => {
        applyUser(unwrapUser(response));
      })
      .catch((error: unknown) => {
        // Tarmoq xatosi bo'lsa refresh tokenni saqlab qolamiz — internet qaytishi mumkin.
        if (error instanceof ApiError && !error.isNetworkError) {
          tokenStore.clear();
        }
        setUser(null);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [applyUser]);

  const permissions = useMemo<ReadonlySet<string>>(
    () => (user ? new Set(user.permissions) : EMPTY_PERMISSIONS),
    [user],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      permissions,
      roleCodes: user?.roleCodes ?? EMPTY_ROLES,
      isLoading,
      login,
      logout,
      reloadUser,
      can: (permission: string) => permissions.has(permission),
    }),
    [user, permissions, isLoading, login, logout, reloadUser],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthContextValue {
  const ctx = use(AuthContext);
  if (!ctx) {
    throw new Error('useAuth faqat <AuthProvider> ichida ishlatiladi');
  }
  return ctx;
}

/**
 * Bitta ruxsatni tekshirish:
 *
 *   const canEditRate = usePermission('fxrate.edit');
 *
 * Ruxsat kodlari EVO_PERMISSION_MATRIX_v1.md §3 dan olinadi.
 */
export function usePermission(permission: string): boolean {
  return useAuth().permissions.has(permission);
}

/** Bir nechta tekshiruv kerak bo'lganda — funksiyaning o'zini oladi. */
export function useCan(): (permission: string) => boolean {
  return useAuth().can;
}

/** Ro'yxatdagi kamida bittasi — menyu guruhlarini filtrlashda ishlatiladi. */
export function useCanAny(permissions: readonly string[]): boolean {
  const can = useCan();
  return permissions.some(can);
}
